

import { defineComponent } from "vue";
import InputField from '@/components/input/inputField.vue';
import CustomButton from '@/components/buttons/customButton.vue';
import BaseAccount from '@/components/account/baseAccount.vue';
import CountryCode from '@/components/input/countryCode.vue';
import ToastMessage from '@/components/feedback/ToastMessage.vue';

import CustomerHelper from '@/helpers/customerHelper.ts';
import { accountService } from '@/services/accountService.ts';

const DEFAULT_ACCOUNT_STATE = () => ({
  id: null,
  name: '',
  surname: '',
  phone: '',
  email: '',
  addressLine1: '',
  postalCode: '',
  city: '',
  town: '',
  country: '',
  dni: ''
});

export default defineComponent({
  name: "AccountSettingsView",
  components: {
    InputField,
    CustomButton,
    BaseAccount,
    CountryCode,
    ToastMessage
  },
  data() {
    return {
      account: DEFAULT_ACCOUNT_STATE(),
      toast: {
        visible: false,
        message: '',
        type: 'success'
      },
      toastTimeoutId: null,
      formErrors: {},
      isLoaded: false,
      emailEditable: false
    };
  },
	mounted() {
		this.loadAccountDetails();
	},
  beforeUnmount() {
    this.clearToastTimeout();
  },
  methods: {
    clearToastTimeout() {
      if (this.toastTimeoutId) {
        clearTimeout(this.toastTimeoutId);
        this.toastTimeoutId = null;
      }
    },
    handleToastClose() {
      this.clearToastTimeout();
      this.toast.visible = false;
    },
    showToast(message, type = 'success') {
      this.clearToastTimeout();
      this.toast = {
        visible: true,
        message,
        type
      };

      this.toastTimeoutId = window.setTimeout(() => {
        this.toast.visible = false;
        this.toastTimeoutId = null;
      }, 4000);
    },
    clearFieldError(field) {
      if (!this.formErrors[field]) {
        return;
      }
      const updatedErrors = { ...this.formErrors };
      delete updatedErrors[field];
      this.formErrors = updatedErrors;
    },
    getFieldError(field) {
      const value = this.account?.[field];
      switch (field) {
        case 'name':
          if (!value || !String(value).trim()) {
            return this.$t('Name is required.');
          }
          break;
        case 'phone': {
          const digits = (value ?? '').toString().replace(/\D+/g, '');
          if (!digits) {
            return this.$t('Phone is required.');
          }
          if (digits.length < 7 || digits.length > 15) {
            return this.$t('Phone must contain between 7 and 15 digits.');
          }
          break;
        }
        case 'email':
          if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
            return this.$t('Please enter a valid email address.');
          }
          break;
        default:
          return '';
      }
      return '';
    },
    validateField(field) {
      const error = this.getFieldError(field);
      if (error) {
        this.formErrors = {
          ...this.formErrors,
          [field]: error
        };
        return false;
      }
      this.clearFieldError(field);
      return true;
    },
    validateForm() {
      const fieldsToValidate = ['name', 'phone', 'email'];
      const errors = {};

      fieldsToValidate.forEach((field) => {
        const error = this.getFieldError(field);
        if (error) {
          errors[field] = error;
        }
      });

      this.formErrors = errors;

      const errorKeys = Object.keys(errors);
      if (errorKeys.length) {
        this.focusFirstInvalid(errorKeys);
        return false;
      }

      return true;
    },
    focusFirstInvalid(errorKeys) {
      const focusMap = {
        name: 'nameField',
        phone: 'phoneField',
        email: 'emailField'
      };

      const targetField = errorKeys.find((key) => focusMap[key]);
      if (!targetField) {
        return;
      }

      this.$nextTick(() => {
        const refName = focusMap[targetField];
        const refComponent = this.$refs[refName];
        const inputEl = refComponent?.$el?.querySelector('input');
        if (inputEl) {
          inputEl.focus();
          inputEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    onEmailInput(value) {
      if (typeof value !== 'string') {
        return;
      }
      const sanitized = value.replace(/\s+/g, '').toLowerCase();
      if (sanitized !== this.account.email) {
        this.account.email = sanitized;
      }
      this.clearFieldError('email');
    },
    toggleEmailEditing() {
      this.emailEditable = !this.emailEditable;
      if (this.emailEditable) {
        this.clearFieldError('email');
        this.$nextTick(() => {
          const inputEl = this.$refs.emailField?.$el?.querySelector('input');
          if (inputEl) {
            inputEl.focus();
            if (typeof inputEl.select === 'function') {
              inputEl.select();
            }
          }
        });
      } else {
        const storedEmail = CustomerHelper.getUser()?.email;
        if (storedEmail) {
          this.account.email = storedEmail;
        }
      }
    },
    syncStoredUser(partialAccount) {
      if (!partialAccount) {
        return;
      }

      const storedUser = CustomerHelper.getUser() || {};
      const updatedUser = { ...storedUser };

      const fieldsToSync = [
        'id',
        'name',
        'surname',
        'email',
        'phone',
        'logo',
        'avatar',
        'image',
        'birthDate',
        'lang',
        'customerTypeId'
      ];

      fieldsToSync.forEach((key) => {
        const value = partialAccount[key];
        if (value !== undefined && value !== null && value !== '') {
          updatedUser[key] = value;
        }
      });

      if (storedUser.token) {
        updatedUser.token = storedUser.token;
      }

      if (partialAccount.logo && !updatedUser.avatar) {
        updatedUser.avatar = partialAccount.logo;
      }

      if (partialAccount.name || partialAccount.surname) {
        const fullName = [partialAccount.name, partialAccount.surname].filter(Boolean).join(' ');
        if (fullName) {
          updatedUser.fullName = fullName;
        }
      }

      CustomerHelper.updateStorageUser(updatedUser);
    },
    async loadAccountDetails(options = {}) {
      const { silent = false } = options;
      try {
        if (!silent) {
          this.isLoaded = false;
        }
        const user = CustomerHelper.getUser();
        if (!user || !user.id) {
          this.$router.push({ name: 'Login' });
          this.emailEditable = false;
          this.isLoaded = true;
          return;
        }

        const accountData = await accountService.getAccountDetails(user.id);

        const rawAccount = accountData?.data ?? accountData;
        const normalizedAccount = rawAccount?.model ?? rawAccount?.data ?? rawAccount;
        if (!normalizedAccount || typeof normalizedAccount !== 'object') {
          throw new Error('Account data not found in response');
        }

        const sanitizedAccount = { ...normalizedAccount };
        delete sanitizedAccount.iban;
        delete sanitizedAccount.accountHolderName;

        const mergedAccount = { ...DEFAULT_ACCOUNT_STATE(), ...(this.account ?? {}), ...sanitizedAccount };
        mergedAccount.phone = typeof mergedAccount.phone === 'string' ? mergedAccount.phone.replace(/\D+/g, '') : '';
        this.account = mergedAccount;
        this.syncStoredUser(mergedAccount);
        this.formErrors = {};
        this.emailEditable = false;
        this.isLoaded = true;

        return mergedAccount;

      } catch (error) {
        if (!silent) {
          this.showToast(this.$t('There was a problem loading your account data.'), 'error');
          this.emailEditable = false;
          this.isLoaded = true;
          return null;
        }
        throw error;
      }
    },
    async saveChanges() {
      if (!this.account) return;

      const isValid = this.validateForm();
      if (!isValid) {
        this.showToast(this.$t('Please review the highlighted fields.'), 'error');
        return;
      }
      
      try {
        const response = await accountService.updateAccountDetails(this.account);

        if (response.ok) {
          this.showToast(this.$t('Account data saved successfully.'), 'success');
          try {
            await this.loadAccountDetails({ silent: true });
          } catch (refreshError) {
            void refreshError;
          }
        } else {
          const errorMsg = response.errorMsg || this.$t('There was a problem saving your data.');
          this.showToast(errorMsg, 'error');
        }
      } catch (error) {
        this.showToast(this.$t('There was a communication error while saving.'), 'error');
      }
    }
  }
});
